// Registro debito tecnico (P1 ops): CRUD SuperAdmin con severità P0-P2 e stati
// APERTO/IN_CORSO/RISOLTO/RINVIATO. Le 2 voci note sono pre-popolate dal seed.
import { useCallback, useEffect, useState } from "react";
import { Wrench, Pencil, Trash2, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SEVERITIES = ["P0", "P1", "P2"];
const STATUSES = ["APERTO", "IN_CORSO", "RISOLTO", "RINVIATO"];
const SEV_STYLE = {
  P0: "bg-red-50 text-red-800 border-red-300",
  P1: "bg-amber-50 text-amber-800 border-amber-300",
  P2: "bg-slate-100 text-slate-600 border-slate-300",
};
const STATUS_STYLE = {
  APERTO: "bg-red-50 text-red-800 border-red-300",
  IN_CORSO: "bg-amber-50 text-amber-800 border-amber-300",
  RISOLTO: "bg-green-50 text-green-800 border-green-300",
  RINVIATO: "bg-slate-100 text-slate-500 border-slate-300",
};
const EMPTY = { title: "", area: "", severity: "P1", status: "APERTO", notes: "" };

export default function PlatformTechDebt() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/platform/tech-debt");
      setItems(data.items || []);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/platform/tech-debt/${editingId}`, form);
        toast.success("Voce aggiornata");
      } else {
        await api.post("/admin/platform/tech-debt", form);
        toast.success("Voce creata");
      }
      setForm(EMPTY);
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({ title: item.title, area: item.area || "", severity: item.severity,
              status: item.status, notes: item.notes || "" });
  };

  const remove = async (item) => {
    try {
      await api.delete(`/admin/platform/tech-debt/${item.id}`);
      toast.success("Voce eliminata");
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const setStatus = async (item, status) => {
    try {
      await api.put(`/admin/platform/tech-debt/${item.id}`, { status });
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  if (error) return <p className="text-sm text-red-700" data-testid="tech-debt-error">{error}</p>;
  if (!items) return <p className="text-sm text-slate-600" data-testid="tech-debt-loading">Caricamento…</p>;

  const open = items.filter((i) => i.status === "APERTO").length;
  const inProgress = items.filter((i) => i.status === "IN_CORSO").length;

  return (
    <div className="space-y-6" data-testid="tech-debt-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Wrench className="w-7 h-7 text-green-800" aria-hidden="true" />
          Tech Debt Register
        </h1>
        <p className="text-sm text-slate-600 mt-1" data-testid="tech-debt-stats">
          {items.length} voci · {open} aperte · {inProgress} in corso
        </p>
      </div>

      <Card className="border-slate-200" data-testid="tech-debt-form-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">
          {editingId ? "Modifica voce" : "Nuova voce"}
        </CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="td-title">Titolo *</Label>
                <Input id="td-title" required minLength={3} value={form.title}
                  data-testid="tech-debt-title-input"
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="td-area">Area (file/componente)</Label>
                <Input id="td-area" value={form.area} data-testid="tech-debt-area-input"
                  onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Severità</Label>
                <Select value={form.severity}
                  onValueChange={(v) => setForm((f) => ({ ...f, severity: v }))}>
                  <SelectTrigger data-testid="tech-debt-severity-select">
                    <SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Stato</Label>
                <Select value={form.status}
                  onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="tech-debt-status-select">
                    <SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="td-notes">Note</Label>
              <Input id="td-notes" value={form.notes} data-testid="tech-debt-notes-input"
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="bg-green-700 hover:bg-green-800"
                data-testid="tech-debt-save-button">
                {editingId ? "Salva modifiche" : "Crea voce"}
              </Button>
              {editingId && (
                <Button type="button" variant="outline" data-testid="tech-debt-cancel-button"
                  onClick={() => { setEditingId(null); setForm(EMPTY); }}>
                  <X className="w-4 h-4 mr-1" aria-hidden="true" /> Annulla
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="tech-debt-list-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">Voci registrate</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {items.length === 0 && (
            <p className="text-xs text-slate-500">Nessuna voce di debito tecnico.</p>)}
          {items.map((item) => (
            <div key={item.id} className="border-b border-slate-100 pb-2 space-y-1"
              data-testid={`tech-debt-item-${item.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={`text-[9px] ${SEV_STYLE[item.severity] || ""}`}
                  data-testid={`tech-debt-severity-${item.id}`}>{item.severity}</Badge>
                <span className="font-medium text-sm text-slate-800">{item.title}</span>
                <span className="flex-1" />
                <Select value={item.status}
                  onValueChange={(v) => setStatus(item, v)}>
                  <SelectTrigger className={`h-7 w-32 text-[10px] ${STATUS_STYLE[item.status] || ""}`}
                    data-testid={`tech-debt-status-${item.id}`}>
                    <SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={() => edit(item)}
                  data-testid={`tech-debt-edit-${item.id}`} aria-label="Modifica">
                  <Pencil className="w-3.5 h-3.5" aria-hidden="true" /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(item)}
                  data-testid={`tech-debt-delete-${item.id}`} aria-label="Elimina">
                  <Trash2 className="w-3.5 h-3.5 text-red-600" aria-hidden="true" /></Button>
              </div>
              {item.area && <p className="text-xs text-slate-500">{item.area}</p>}
              {item.notes && <p className="text-xs text-slate-400">{item.notes}</p>}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
